import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Container,
  Box,
  Paper,
  Button,
  useTheme,
} from "@mui/material";
import "../styles/pipeline.css";
const LoadingIcon = ({ color }) => (
  <div className="icon">
    <svg
      className="loading-icon"
      width="24"
      height="24"
      viewBox="0 0 50 50"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle
        cx="25"
        cy="25"
        r="20"
        stroke={color} /* Blue stroke */
        fill="none"
        strokeWidth="5"
        strokeDasharray="31.4"
        strokeDashoffset="0"
        strokeLinecap="round"
      />
    </svg>
  </div>
);

const CheckmarkIcon = () => (
  <svg
    className="checkmark"
    width="17"
    height="13"
    viewBox="0 0 17 13"
    xmlns="http://www.w3.org/2000/svg"
  >
    <polyline
      points="1 7 5 11 15 1"
      stroke="#4caf50"
      strokeWidth="2"
      fill="none"
      strokeDasharray="20"
      strokeDashoffset="20"
    >
      <animate
        attributeName="stroke-dashoffset"
        from="20"
        to="0"
        dur="0.5s"
        begin="0.2s"
        fill="freeze"
      />
    </polyline>
  </svg>
);

const FailedIcon = () => <span className="icon">❌</span>;
const SkippedIcon = () => <span className="icon text-gray-400">━</span>;

const PipelineRunDetails = () => {
  const { runId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const token = localStorage.getItem('access_token');
  const orgType= localStorage.getItem('org_type');
  const stages = [
    {
      name: "build",
      label: "Build",
    },
    {
      name: "static-analysis",
      label: "Static Analysis",
      substages: [], // To be dynamically populated based on fetched tool_id
    },
    {
      name: "unit-testing",
      label: "Unit Testing",
      substages: [], // Dynamically populated
    },
    {
      name: "memory-testing",
      label: "Memory Testing",
      substages: [], // Dynamically populated
    },
    {
      name: "cyber-testing",
      label: "Cyber Testing",
      substages: [], // Dynamically populated
    },
    {
      name: "foss-analysis",
      label: "FOSS Analysis",
      substages: [], // Dynamically populated
    },
  ];

  const [statuses, setStatuses] = useState({
    build: { buildStatus: "waiting" },
    "static-analysis": {
      tool1: "waiting",
    },
    "unit-testing": {
      tool1: "waiting",
    },
    "memory-testing": {
      tool1: "waiting",
    },
    "cyber-testing": {
      tool1: "waiting",
    },
    "foss-analysis": {
      tool1: "waiting",
    },
  });

  const [staticAnalysisTools, setStaticAnalysisTools] = useState([
    { tool_id: "tool1", toolName: "Loading..." },
  ]);
  const [unitTestingTools, setUnitTestingTools] = useState([
    { tool_id: "tool1", toolName: "Loading..." },
  ]);
  const [memoryTestingTools, setMemoryTestingTools] = useState([
    { tool_id: "tool1", toolName: "Loading..." },
  ]);
  const [cyberSecurityAnalysisTools, setCyberSecurityAnalysisTools] = useState([
    { tool_id: "tool1", toolName: "Loading..." },
  ]);
  const [fossComplianceTools, setFossComplianceTools] = useState([
    { tool_id: "tool1", toolName: "Loading..." },
  ]);

  const [intervalId, setIntervalId] = useState(null);
  const [isCancelled, setIsCancelled] = useState(false);

  const shouldStopFetching = (statusData) => {
    // Define terminal statuses that indicate we should stop fetching
    const terminalStatuses = ["skipped", "failed", "completed"];
    const inProgressStatuses = ["waiting", "initialized", "inprogress"];

    // Check if any stage has an in-progress status
    const hasInProgressStatus = Object.values(statusData).some(stageStatuses =>
      Object.values(stageStatuses).some(status =>
        inProgressStatuses.includes(status)
      )
    );

    // If any stage is still in progress, continue fetching
    if (hasInProgressStatus) {
      return false;
    }

    // Check if all stages have terminal statuses
    const allTerminalStatuses = Object.values(statusData).every(stageStatuses =>
      Object.values(stageStatuses).every(status =>
        terminalStatuses.includes(status)
      )
    );

    // If all stages have terminal statuses, stop fetching
    return allTerminalStatuses;
  };

  const fetchStatus = async () => {
    if (isCancelled) return;
    const apiUrl = `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/run/status`;
    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ run_id: runId }),
      });
      const data = await response.json();

      // Update statuses
      const updatedStatuses = {
        build: { buildStatus: data.buildStatus },
        "static-analysis": {},
        "unit-testing": {},
        "memory-testing": {},
        "cyber-testing": {},
        "foss-analysis": {},
      };

      // Populate static analysis tools dynamically
      const staticAnalysisTools = Object.entries(
        data.staticAnalysisStatuses || {}
      ).map(([tool_id, toolData]) => ({
        tool_id,
        ...toolData,
      }));
      setStaticAnalysisTools(staticAnalysisTools);

      staticAnalysisTools.forEach((tool) => {
        updatedStatuses["static-analysis"][tool.tool_id] = tool.status;
      });

      // Populate unit testing tools dynamically
      const unitTestingTools = Object.entries(data.unitTestingStatuses || {}).map(
        ([tool_id, toolData]) => ({
          tool_id,
          ...toolData,
        })
      );
      setUnitTestingTools(unitTestingTools);

      unitTestingTools.forEach((tool) => {
        updatedStatuses["unit-testing"][tool.tool_id] = tool.status;
      });

      // Populate memory testing tools dynamically
      const memoryTestingTools = Object.entries(data.memoryTestingStatuses || {}).map(
        ([tool_id, toolData]) => ({
          tool_id,
          ...toolData,
        })
      );
      setMemoryTestingTools(memoryTestingTools);

      memoryTestingTools.forEach((tool) => {
        updatedStatuses["memory-testing"][tool.tool_id] = tool.status;
      });

      const cyberSecurityAnalysisTools = Object.entries(data.cybersecurityAnalysisStatuses || {}).map(
        ([tool_id, toolData]) => ({
          tool_id,
          ...toolData,
        })
      );
      setCyberSecurityAnalysisTools(cyberSecurityAnalysisTools);

      cyberSecurityAnalysisTools.forEach((tool) => {
        updatedStatuses["cyber-testing"][tool.tool_id] = tool.status;
      });


      // Update foss analysis statuses
      const fossComplianceTools = Object.entries(
        data.fossComplianceStatuses || {}
      ).map(([tool_id, toolData]) => ({
        tool_id,
        ...toolData,
      }));
      setFossComplianceTools(fossComplianceTools);
      fossComplianceTools.forEach((tool) => {
        updatedStatuses["foss-analysis"][tool.tool_id] = tool.status;
      });

      setStatuses(updatedStatuses);

      // Check if we should stop fetching based on all statuses
      if (shouldStopFetching(updatedStatuses)) {
        clearInterval(intervalId);
        setIsCancelled(true);
      }
    } catch (error) {
      console.error("Error fetching status:", error);
    }
  };

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(() => {
      if (!isCancelled) fetchStatus();
    }, 5000);

    setIntervalId(interval);
    return () => clearInterval(interval);
  }, [isCancelled]);

  const handleCancelClick = () => {
    clearInterval(intervalId);
    setIsCancelled(true);
  };

  return (
    <Box className="pipeline-container-horizontal" sx={{
    backgroundColor: isDarkMode ? "#1e1e1e" : "inherit",
    p: 2,
    borderRadius: 2,
  }}>
      {stages.map((stage, stageIndex) => (
        <div key={stage.name} className="pipeline-stage">
          <div className="pipeline-stage-label">
            <h3>{stage.label}</h3>
          </div>
          <div className="pipeline-substages" style={{ backgroundColor: isDarkMode ? "#1f2937" : "inherit"}}>
            {stage.name === "build" && (
              <>
                {statuses.build.buildStatus === "skipped" ? (
                  <div className="pipeline-node skipped">
                    <div className="pipeline-status-icon">
                      <SkippedIcon />
                    </div>
                    <span className="text-gray-400 text-xs">Skipped</span>
                  </div>
                ) : (
                  <div className={`pipeline-node ${statuses.build.buildStatus}`}>
                    <div className="pipeline-status-icon">
                      {statuses.build.buildStatus === "waiting" && (
                        <LoadingIcon color="#fdd835" />
                      )}
                      {statuses.build.buildStatus === "initialized" && (
                        <LoadingIcon color="#1976d2" />
                      )}
                      {statuses.build.buildStatus === "inprogress" && (
                        <LoadingIcon color="#1976d2" />
                      )}
                      {statuses.build.buildStatus === "completed" && (
                        <CheckmarkIcon />
                      )}
                      {statuses.build.buildStatus === "failed" && <FailedIcon />}
                    </div>
                  </div>
                )}
              </>
            )}
            {stage.name === "static-analysis" && (
              <>
                {staticAnalysisTools.length === 0 ? (
                  <div className="pipeline-node skipped">
                    <div className="pipeline-status-icon">
                      <SkippedIcon />
                    </div>
                    <span className="text-gray-400 text-xs">Skipped</span>
                  </div>
                ) : (
                  staticAnalysisTools.map((tool) => (
                    <div
                      key={tool.tool_id}
                      className={`pipeline-node ${statuses["static-analysis"][tool.tool_id] === "skipped"
                        ? "skipped"
                        : statuses["static-analysis"][tool.tool_id] || "waiting"
                        }`}
                    >
                      <div className="pipeline-status-icon">
                        {statuses["static-analysis"][tool.tool_id] === "skipped" && (
                          <SkippedIcon />
                        )}
                        {statuses["static-analysis"][tool.tool_id] === "waiting" && (
                          <LoadingIcon color="#fdd835" />
                        )}
                        {statuses["static-analysis"][tool.tool_id] === "initialized" && (
                          <LoadingIcon color="#1976d2" />
                        )}
                        {statuses["static-analysis"][tool.tool_id] === "inprogress" && (
                          <LoadingIcon color="#1976d2" />
                        )}
                        {statuses["static-analysis"][tool.tool_id] === "completed" && (
                          <CheckmarkIcon />
                        )}
                        {statuses["static-analysis"][tool.tool_id] === "failed" && (
                          <FailedIcon />
                        )}
                      </div>
                      <p style={{ color: isDarkMode ? "#ffffff" : "#ffffff"}}>{orgType==="O"?tool.toolName:tool.GenerictoolName}{statuses["static-analysis"][tool.tool_id] === "skipped" ? " (Skipped)" : ""}</p>
                    </div>
                  ))
                )}
              </>
            )}
            {stage.name === "unit-testing" && (
              <>
                {unitTestingTools.length === 0 ? (
                  <div className="pipeline-node skipped">
                    <div className="pipeline-status-icon">
                      <SkippedIcon />
                    </div>
                    <span className="text-gray-400 text-xs">Skipped</span>
                  </div>
                ) : (
                  unitTestingTools.map((tool) => (
                    <div
                      key={tool.tool_id}
                      className={`pipeline-node ${statuses["unit-testing"][tool.tool_id] === "skipped"
                        ? "skipped"
                        : statuses["unit-testing"][tool.tool_id] || "waiting"
                        }`}
                    >
                      <div className="pipeline-status-icon">
                        {statuses["unit-testing"][tool.tool_id] === "skipped" && (
                          <SkippedIcon />
                        )}
                        {statuses["unit-testing"][tool.tool_id] === "waiting" && (
                          <LoadingIcon color="#fdd835" />
                        )}
                        {statuses["unit-testing"][tool.tool_id] === "initialized" && (
                          <LoadingIcon color="#1976d2" />
                        )}
                        {statuses["unit-testing"][tool.tool_id] === "inprogress" && (
                          <LoadingIcon color="#1976d2" />
                        )}
                        {statuses["unit-testing"][tool.tool_id] === "completed" && (
                          <CheckmarkIcon />
                        )}
                        {statuses["unit-testing"][tool.tool_id] === "failed" && (
                          <FailedIcon />
                        )}
                      </div>
                      <p style={{ color: isDarkMode ? "#ffffff" : "#ffffff"}}>{orgType==="O"?tool.toolName:tool.GenerictoolName}{statuses["unit-testing"][tool.tool_id] === "skipped" ? " (Skipped)" : ""}</p>
                    </div>
                  ))
                )}
              </>
            )}
            {stage.name === "memory-testing" && (
              <>
                {memoryTestingTools.length === 0 ? (
                  <div className="pipeline-node skipped">
                    <div className="pipeline-status-icon">
                      <SkippedIcon />
                    </div>
                    <span className="text-gray-400 text-xs">Skipped</span>
                  </div>
                ) : (
                  memoryTestingTools.map((tool) => (
                    <div
                      key={tool.tool_id}
                      className={`pipeline-node ${statuses["memory-testing"][tool.tool_id] === "skipped"
                        ? "skipped"
                        : statuses["memory-testing"][tool.tool_id] || "waiting"
                        }`}
                    >
                      <div className="pipeline-status-icon">
                        {statuses["memory-testing"][tool.tool_id] === "skipped" && (
                          <SkippedIcon />
                        )}
                        {statuses["memory-testing"][tool.tool_id] === "waiting" && (
                          <LoadingIcon color="#fdd835" />
                        )}
                        {statuses["memory-testing"][tool.tool_id] === "initialized" && (
                          <LoadingIcon color="#1976d2" />
                        )}
                        {statuses["memory-testing"][tool.tool_id] === "inprogress" && (
                          <LoadingIcon color="#1976d2" />
                        )}
                        {statuses["memory-testing"][tool.tool_id] === "completed" && (
                          <CheckmarkIcon />
                        )}
                        {statuses["memory-testing"][tool.tool_id] === "failed" && (
                          <FailedIcon />
                        )}
                      </div>
                      <p style={{ color: isDarkMode ? "#ffffff" : "#ffffff"}}>{orgType==="O"?tool.toolName:tool.GenerictoolName}{statuses["memory-testing"][tool.tool_id] === "skipped" ? " (Skipped)" : ""}</p>
                    </div>
                  ))
                )}
              </>
            )}
            {stage.name === "cyber-testing" && (
              <>
                {cyberSecurityAnalysisTools.length === 0 ? (
                  <div className="pipeline-node skipped">
                    <div className="pipeline-status-icon">
                      <SkippedIcon />
                    </div>
                    <span className="text-gray-400 text-xs">Skipped</span>
                  </div>
                ) : (
                  cyberSecurityAnalysisTools.map((tool) => (
                    <div
                      key={tool.tool_id}
                      className={`pipeline-node ${statuses["cyber-testing"][tool.tool_id] === "skipped"
                        ? "skipped"
                        : statuses["cyber-testing"][tool.tool_id] || "waiting"
                        }`}
                    >
                      <div className="pipeline-status-icon">
                        {statuses["cyber-testing"][tool.tool_id] === "skipped" && (
                          <SkippedIcon />
                        )}
                        {statuses["cyber-testing"][tool.tool_id] === "waiting" && (
                          <LoadingIcon color="#fdd835" />
                        )}
                        {statuses["cyber-testing"][tool.tool_id] === "initialized" && (
                          <LoadingIcon color="#1976d2" />
                        )}
                        {statuses["cyber-testing"][tool.tool_id] === "inprogress" && (
                          <LoadingIcon color="#1976d2" />
                        )}
                        {statuses["cyber-testing"][tool.tool_id] === "completed" && (
                          <CheckmarkIcon />
                        )}
                        {statuses["cyber-testing"][tool.tool_id] === "failed" && (
                          <FailedIcon />
                        )}
                      </div>
                      <p style={{ color: isDarkMode ? "#ffffff" : "#ffffff"}}>{orgType==="O"?tool.toolName:tool.GenerictoolName}{statuses["cyber-testing"][tool.tool_id] === "skipped" ? " (Skipped)" : ""}</p>
                    </div>
                  ))
                )}
              </>
            )}
            {stage.name === "foss-analysis" && (
              <>
                {fossComplianceTools.length === 0 ? (
                  <div className="pipeline-node skipped">
                    <div className="pipeline-status-icon">
                      <SkippedIcon />
                    </div>
                    <span className="text-gray-400 text-xs">Skipped</span>
                  </div>
                ) : (
                  fossComplianceTools.map((tool) => (
                    <div
                      key={tool.tool_id}
                      className={`pipeline-node ${statuses["foss-analysis"][tool.tool_id] === "skipped"
                        ? "skipped"
                        : statuses["foss-analysis"][tool.tool_id] || "waiting"
                        }`}
                    >
                      <div className="pipeline-status-icon">
                        {statuses["foss-analysis"][tool.tool_id] === "skipped" && (
                          <SkippedIcon />
                        )}
                        {statuses["foss-analysis"][tool.tool_id] === "waiting" && (
                          <LoadingIcon color="#fdd835" />
                        )}
                        {statuses["foss-analysis"][tool.tool_id] === "initialized" && (
                          <LoadingIcon color="#1976d2" />
                        )}
                        {statuses["foss-analysis"][tool.tool_id] === "inprogress" && (
                          <LoadingIcon color="#1976d2" />
                        )}
                        {statuses["foss-analysis"][tool.tool_id] === "completed" && (
                          <CheckmarkIcon />
                        )}
                        {statuses["foss-analysis"][tool.tool_id] === "failed" && (
                          <FailedIcon />
                        )}
                      </div>
                      <p style={{ color: isDarkMode ? "#ffffff" : "#ffffff"}}>{orgType==="O"?tool.toolName:tool.GenerictoolName}{statuses["foss-analysis"][tool.tool_id] === "skipped" ? " (Skipped)" : ""}</p>
                    </div>
                  ))
                )}
              </>
            )}
          </div>

          {/* Pipeline Connections */}
          {stageIndex < stages.length - 1 && (
            <div className="pipeline-connector">
              {stage.substages
                ? stage.substages.map((substage, subIndex) => (
                  <div
                    key={`${substage}-connector`}
                    className={`pipeline-bg ${statuses[substage] === "completed" ? "active" : ""
                      }`}
                  ></div>
                ))
                : null}
            </div>
          )}
        </div>
      ))}
      {/* {!isCancelled && (
        <Button
          variant="contained"
          className="athrv-dash-primary-button"
          onClick={handleCancelClick}
        >
          Cancel
        </Button>
      )} */}
    </Box>
  );
};

export default PipelineRunDetails;
// export default memo(Pipeline);
